import { Chip, Stack } from "@mui/material";
import { fetchSlaKpi, fetchSlaSettings } from "api";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import { SLA_MONITOR_TEXT_KEYS } from "model/slaMonitorModel";
import type { SlaKpiRow } from "model/slaModel";
import React, { useEffect, useRef, useState } from "react";

/**
 * **KPI SLA nell'intestazione dei processi vivi** (Operazioni > In tempo reale).
 *
 * Prende il posto del titolo "Monitor dei processi": quattro chip compatti
 * (totale, in tempo, a rischio, violati) con i conteggi di
 * `GET /sla/organizations/{org}/kpi`.
 *
 * Regole:
 * - si mostra **solo** se lo SLA dell'organizzazione e' attivo
 *   (`GET /sla/organizations/{org}/settings` -> `enabled`). SLA disattivo,
 *   errore o 404 sulle impostazioni: rende `fallback` (titolo e chip del
 *   conteggio di sempre). Con i KPI il conteggio sparisce: c'e' gia' "Totale";
 * - i chip in tempo / a rischio / violati sono filtri a interruttore della
 *   lista viva (`sla_health` su `/statistics/v2/current`): un clic seleziona,
 *   un secondo clic o "Totale" toglie il filtro. Il chip selezionato e' pieno,
 *   gli altri contornati; sono pulsanti (`aria-pressed`) raggiungibili da
 *   tastiera;
 * - i conteggi restano quelli dell'organizzazione e contano **istanze** SLA
 *   aperte (processo e attivita'), mentre il filtro mette ogni processo nella
 *   salute peggiore delle sue istanze: il numero di righe filtrate non coincide
 *   per forza con il chip;
 * - si rilegge quando la lista viva si rilegge (`refreshKey`, incrementato da
 *   `ProcessStarted` a ogni caricamento della prima pagina) e al cambio di
 *   organizzazione. Le riletture sono raggruppate (debounce) e una sola
 *   richiesta e' in volo alla volta: nessuna raffica di chiamate quando i
 *   filtri cambiano in rapida successione;
 * - stessi colori dei chip SLA esistenti (`SlaTaskSlaBadge` compatto): verde
 *   neutro, verde outlined, arancio, rosso.
 */
interface SlaLiveKpiChipsProps {
  idorganization: number;
  /** Cambia quando la lista viva si rilegge. */
  refreshKey: number;
  /** Salute SLA filtrata (0, 1, 2) o `null` senza filtro. */
  selectedHealth: number | null;
  /** Nuovo filtro: `null` lo toglie. */
  onSelectHealth: (health: number | null) => void;
  /** Esito della lettura delle impostazioni: `false` se SLA disattivo o errore. */
  onAvailabilityChange?: (enabled: boolean) => void;
  /** Cosa rendere quando i KPI non si mostrano (il titolo di sempre). */
  fallback: React.ReactNode;
}

/** Finestra di raggruppamento delle riletture. */
const KPI_REFRESH_DEBOUNCE_MS = 800;

/**
 * Riga dei KPI: respiro verticale di `theme.spacing(1)` sopra e sotto (la riga
 * stava a ridosso della tabella) e altezza minima fissa (titolo h6 ~32px +
 * padding), uguale in caricamento e a KPI letti.
 */
const KPI_ROW_SX = { py: 1, minHeight: 48, boxSizing: "border-box" } as const;

/**
 * Ultimo esito noto di "SLA attivo" per organizzazione (solo comodita' del
 * browser): all'ingresso nella vista viva i chip compaiono subito invece di
 * mostrare prima il titolo "Monitor dei processi" e poi sostituirlo. La lettura
 * delle impostazioni resta la fonte della verita' e corregge il valore.
 */
const SLA_ENABLED_CACHE_KEY = "flowenti-sla-live-enabled";
const readCachedEnabled = (idorganization: number): boolean => {
  try {
    const raw = localStorage.getItem(SLA_ENABLED_CACHE_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    return map[String(idorganization)] === true;
  } catch {
    return false;
  }
};
const writeCachedEnabled = (idorganization: number, enabled: boolean): void => {
  try {
    const raw = localStorage.getItem(SLA_ENABLED_CACHE_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    map[String(idorganization)] = enabled;
    localStorage.setItem(SLA_ENABLED_CACHE_KEY, JSON.stringify(map));
  } catch {
    // Storage non disponibile: si attende la lettura delle impostazioni.
  }
};

/** Il Server oggi risponde con un oggetto; un array legacy resta accettato. */
const toKpiRow = (kpi: unknown): SlaKpiRow | null => {
  const row = Array.isArray(kpi) ? kpi[0] : kpi;
  return row && typeof row === "object" ? (row as SlaKpiRow) : null;
};

const SlaLiveKpiChips: React.FC<SlaLiveKpiChipsProps> = ({
  idorganization,
  refreshKey,
  selectedHealth,
  onSelectHealth,
  onAvailabilityChange,
  fallback,
}) => {
  const translations = useTranslations();
  const availabilityRef = useRef(onAvailabilityChange);
  availabilityRef.current = onAvailabilityChange;
  const [enabled, setEnabled] = useState<boolean>(() => readCachedEnabled(idorganization));
  const [kpi, setKpi] = useState<SlaKpiRow | null>(null);

  /** Organizzazione corrente: le risposte di un'organizzazione precedente si scartano. */
  const orgRef = useRef<number>(idorganization);
  const inFlightRef = useRef<boolean>(false);
  const pendingRef = useRef<boolean>(false);

  // Impostazioni SLA: una sola lettura per organizzazione.
  useEffect(() => {
    orgRef.current = idorganization;
    const cached = readCachedEnabled(idorganization);
    setEnabled(cached);
    // Esito noto in cache: colonne e chip si mostrano subito, la lettura conferma.
    if (cached) availabilityRef.current?.(true);
    setKpi(null);
    let cancelled = false;
    (async () => {
      try {
        const settings = await fetchSlaSettings(idorganization);
        if (cancelled) return;
        const isEnabled = settings?.enabled === true;
        writeCachedEnabled(idorganization, isEnabled);
        setEnabled(isEnabled);
        availabilityRef.current?.(isEnabled);
      } catch {
        if (cancelled) return;
        writeCachedEnabled(idorganization, false);
        setEnabled(false);
        availabilityRef.current?.(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [idorganization]);

  // KPI: al primo abilitarsi e a ogni rilettura della lista, con debounce e dedupe.
  useEffect(() => {
    if (!enabled) return;
    const org = idorganization;

    const load = async () => {
      if (inFlightRef.current) {
        pendingRef.current = true;
        return;
      }
      inFlightRef.current = true;
      try {
        const response = await fetchSlaKpi(org);
        if (orgRef.current === org) setKpi(toKpiRow(response?.kpi));
      } catch {
        // Errore: si tengono gli ultimi valori letti; senza valori non si rende nulla.
      } finally {
        inFlightRef.current = false;
        if (pendingRef.current && orgRef.current === org) {
          pendingRef.current = false;
          load();
        }
      }
    };

    const timer = window.setTimeout(load, KPI_REFRESH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [enabled, idorganization, refreshKey]);

  // SLA spento: l'intestazione di sempre, senza margini aggiunti.
  if (!enabled) return <>{fallback}</>;
  // SLA attivo ma KPI non ancora letti: gli stessi chip con "—" al posto dei
  // conteggi (niente titolo che compare e poi sparisce); i chip sono gia'
  // cliccabili e "Totale" e' gia' selezionato.

  const chips: Array<{
    id: string;
    /** Salute filtrata dal chip; `null` per "Totale" (toglie il filtro). */
    health: number | null;
    labelKey: string;
    count: number | undefined;
    color: "success" | "warning" | "error" | "default";
  }> = [
    { id: "total", health: null, labelKey: SLA_MONITOR_TEXT_KEYS.kpiTotal, count: kpi?.total, color: "default" },
    { id: "on-time", health: 0, labelKey: SLA_MONITOR_TEXT_KEYS.kpiOnTime, count: kpi?.on_time, color: "success" },
    { id: "at-risk", health: 1, labelKey: SLA_MONITOR_TEXT_KEYS.kpiAtRisk, count: kpi?.at_risk, color: "warning" },
    { id: "breached", health: 2, labelKey: SLA_MONITOR_TEXT_KEYS.kpiBreached, count: kpi?.breached, color: "error" },
  ];

  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      role="group"
      data-testid="sla-live-kpi"
      data-kpi-as-of={kpi?.as_of ?? ""}
      aria-busy={!kpi}
      sx={KPI_ROW_SX}
    >
      {chips.map((chip) => {
        // "Totale" e' il chip di "nessun filtro": selezionato quando la salute
        // filtrata e' assente (default all'ingresso nella vista viva).
        const pressed =
          chip.health === null ? selectedHealth == null : chip.health === selectedHealth;
        return (
          <Chip
            key={chip.id}
            size="medium"
            color={chip.color}
            variant={pressed ? "filled" : "outlined"}
            data-testid={`sla-live-kpi-${chip.id}`}
            aria-pressed={pressed}
            // MUI rende il chip cliccabile un role="button" con tabIndex 0:
            // Invio e Spazio lo attivano.
            onClick={() =>
              onSelectHealth(chip.health === null || pressed ? null : chip.health)
            }
            label={`${getTranslation(translations, chip.labelKey)} ${
              typeof chip.count === "number" ? chip.count : "—"
            }`}
            sx={{ height: 30, fontSize: 14, fontWeight: 700 }}
          />
        );
      })}
    </Stack>
  );
};

export default SlaLiveKpiChips;
